import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = authService.getStoredUser();
    const token = authService.getToken();
    
    if (storedUser && token) {
      setUser(storedUser);
      // Weryfikuj token z backendem
      authService.getCurrentUser()
        .then((currentUser) => {
          setUser(currentUser);
        })
        .catch(() => {
          authService.logout();
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, twoFactorCode = null) => {
    try {
      setLoading(true);
      const data = await authService.login(email, password, twoFactorCode);
      
      // Jeśli wymagany jest kod 2FA, zwróć specjalną odpowiedź
      if (data.requiresTwoFactor) {
        return { 
          requiresTwoFactor: true, 
          message: data.message 
        };
      }
      
      // Normalne logowanie
      if (data.user && data.accessToken) {
        setUser(data.user);
        return {
          success: true,
          requiresMfaSetup: data.requiresMfaSetup || data.user.requiresMfaSetup,
        };
      }

      return { success: false, error: 'Nieprawidłowa odpowiedź z serwera' };
    } catch (error) {
      console.error('Błąd logowania:', error);
      
      // Obsługa timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return { 
          success: false, 
          error: 'Przekroczono czas oczekiwania. Sprawdź połączenie z internetem lub spróbuj ponownie za chwilę.' 
        };
      }
      
      // Obsługa błędów sieciowych
      if (error.message === 'Network Error' || !error.response) {
        return { 
          success: false, 
          error: 'Nie można połączyć się z serwerem. Sprawdź połączenie z internetem.' 
        };
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Błąd logowania';
      const accountLocked = error.response?.data?.accountLocked;
      const minutesRemaining = error.response?.data?.minutesRemaining;
      return { success: false, error: errorMessage, accountLocked, minutesRemaining };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    localStorage.removeItem('impersonatedBy');
    localStorage.removeItem('originalSuperAdmin');
    setUser(null);
  };

  const impersonate = async (userId) => {
    try {
      setLoading(true);
      const data = await authService.impersonate(userId);
      
      if (data.user && data.accessToken) {
        setUser(data.user);
        return { success: true };
      }
      
      return { success: false, error: 'Nieprawidłowa odpowiedź z serwera' };
    } catch (error) {
      console.error('Błąd impersonacji:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Błąd podczas logowania jako użytkownik';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const returnToSuperAdmin = async () => {
    try {
      setLoading(true);
      const data = await authService.returnToSuperAdmin();
      
      if (data && data.user && data.accessToken) {
        setUser(data.user);
        return { success: true };
      }
      
      return { success: false, error: 'Nieprawidłowa odpowiedź z serwera' };
    } catch (error) {
      console.error('Błąd powrotu do super admina:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Błąd podczas powrotu do super admina';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
    localStorage.setItem('user', JSON.stringify(currentUser));
    return currentUser;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout,
      refreshUser,
      impersonate, 
      returnToSuperAdmin,
      isImpersonating: authService.isImpersonating(),
      originalSuperAdmin: authService.getOriginalSuperAdmin()
    }}>
      {children}
    </AuthContext.Provider>
  );
};
